import React from "react";
import { compiler } from "markdown-to-jsx";
import { makeStyles } from "@material-ui/core/styles";

import { splitMonospaceBlocks, whatsappToMarkdown } from "../../utils/whatsappMarkdown";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
	<a {...props} target="_blank" rel="noopener noreferrer">
		{children}
	</a>
);

// Corpos muito grandes (ex: emails) estouram a pilha do parser recursivo do
// markdown-to-jsx ("InternalError: too much recursion" no Firefox).
const MAX_MARKDOWN_LENGTH = 10000;

// O markdown-to-jsx testa "<..." (autolink/HTML) e "[..." (link/imagem) com
// regex que fazem backtracking ~cúbico: 10 KB de "<http://a" ou "[a](" levam
// 8–14 s para compilar e congelam a aba ("Página sem resposta") — XML colado no
// chat é o caso real. O WhatsApp não tem nenhuma dessas construções, então texto
// longo cheio delas vai direto para texto puro sem perder formatação legítima.
const RISKY_MARKDOWN_MIN_LENGTH = 1500;
const MAX_RISKY_MARKDOWN_CHARS = 20;

const isRiskyForMarkdown = text => {
	if (text.length < RISKY_MARKDOWN_MIN_LENGTH) return false;
	let count = 0;
	for (let index = 0; index < text.length; index += 1) {
		const char = text[index];
		if ((char === "<" || char === "[") && ++count > MAX_RISKY_MARKDOWN_CHARS) return true;
	}
	return false;
};

const useStyles = makeStyles(theme => ({
	// <pre> é bloco; o wrapper inline precisa acompanhar para não vazar do balão.
	blockWrapper: {
		display: "block",
	},
	codeBlock: {
		margin: theme.spacing(0.5, 0),
		padding: theme.spacing(0.75, 1),
		borderRadius: 4,
		backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)",
		fontFamily: "monospace",
		fontSize: "0.8125rem",
		lineHeight: 1.45,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		overflowX: "auto",
		maxWidth: "100%",
	},
}));

const PlainTextFallback = ({ text }) => (
	<span translate="no" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
		{text}
	</span>
);

const MarkdownWrapper = ({ children, codeBlocks = false }) => {
	const classes = useStyles();
	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: { component: CustomLink },
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, []);

	if (!children) return null;
	if (typeof children !== "string") {
		return <span translate="no">{children}</span>;
	}

	if (children.length > MAX_MARKDOWN_LENGTH || isRiskyForMarkdown(children)) {
		return <PlainTextFallback text={children} />;
	}

	// Só o chat pede blocos; nas prévias de uma linha (lista de tickets, drawer)
	// a cerca ``` continua virando code span inline.
	const segments = codeBlocks ? splitMonospaceBlocks(children) : [{ type: "text", value: children }];
	const hasBlock = segments.some(segment => segment.type === "block");

	// compiler() faz o parse sincronamente aqui dentro (diferente de <Markdown>,
	// cujo parse rodaria no render do filho, fora do alcance deste try/catch).
	let compiled;
	try {
		compiled = segments.map((segment, index) =>
			segment.type === "block" ? (
				<pre key={`block-${index}`} className={classes.codeBlock}>
					{segment.value}
				</pre>
			) : (
				<React.Fragment key={`text-${index}`}>
					{compiler(whatsappToMarkdown(segment.value), options)}
				</React.Fragment>
			)
		);
	} catch (err) {
		return <PlainTextFallback text={children} />;
	}

	return (
		<span translate="no" className={hasBlock ? classes.blockWrapper : undefined}>
			{compiled}
		</span>
	);
};

// A lista de mensagens re-renderiza inteira a cada mudança de estado; sem memo
// cada mensagem seria recompilada toda vez (children é string → comparação barata).
export default React.memo(MarkdownWrapper);
